import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  ExternalLink,
  Copy,
  FileDown,
  RotateCcw,
  Loader2,
  Info,
  Code2,
  ShieldQuestion,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  Maximize2,
  ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { TuneTypeBadge } from "../components/TuneTypeBadge";
import { AppHeader } from "../components/AppHeader";
import { AbcNotation } from "../components/AbcNotation";
import { AbcPlayer } from "../components/AbcPlayer";
import { AiExpertCard } from "../components/AiExpertCard";
import { FullscreenOverlay } from "../components/FullscreenOverlay";
import { PdfExportDialog } from "../components/PdfExportDialog";
import { FlagButton } from "../components/FlagButton";
import { FoundLinks } from "../components/FoundLinks";
import { AbcHelpDialog } from "../components/AbcHelpDialog";
import { RankingVote } from "../components/RankingVote";
import { getTune, verifyRarity } from "../lib/api";
import { buildAbc, prettyKey, transposeAbc } from "../lib/abcUtils";
import { isFavourite, toggleFavourite, favKey, getFavourite, favouritesForTune } from "../lib/favourites";
import { getFlag, flagsForTune } from "../lib/flags";

const VERDICT_STYLES = {
  confirmed_rare: { bg: "#ecfdf5", border: "#4CAF50", icon: CheckCircle2, color: "#166534", label: "Confirmed rare" },
  true_homonym: { bg: "#eff6ff", border: "#0EA5E9", icon: ShieldQuestion, color: "#075985", label: "True homonym" },
  false_rare: { bg: "#fef2f2", border: "#F43F5E", icon: AlertTriangle, color: "#9f1239", label: "Possible false rare" },
};

export default function TunePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tune, setTune] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingIdx, setSettingIdx] = useState(0);
  const [abc, setAbc] = useState("");
  const [showAbc, setShowAbc] = useState(false);
  const [transposeSteps, setTransposeSteps] = useState(0);
  const [notationZoom, setNotationZoom] = useState(100);
  const [infoOpen, setInfoOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [abcFull, setAbcFull] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [fav, setFav] = useState(false);
  const [rarity, setRarity] = useState(null);
  const [savedMarks, setSavedMarks] = useState({});
  const [rarityLoading, setRarityLoading] = useState(false);
  const versionRowRef = useRef(null);
  const originalAbcRef = useRef("");
  const pendingFavAbc = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setLoading(true);
    getTune(id)
      .then((t) => {
        setTune(t);
        setShowAbc(false);
        const favParam = searchParams.get("fav");
        // a snapshot key can belong to a favourite or to a flag
        const saved = favParam ? getFavourite(favParam) || getFlag(favParam) : null;
        if (saved) {
          pendingFavAbc.current = saved.edited && saved.abc ? saved.abc : null;
          setSettingIdx(Math.max(0, Math.min((saved.version || 1) - 1, (t.settings?.length || 1) - 1)));
        } else {
          const v = parseInt(searchParams.get("v"), 10) || 1;
          setSettingIdx(Math.max(0, Math.min(v - 1, (t.settings?.length || 1) - 1)));
        }
      })
      .catch(() => toast.error("Tune not found"))
      .finally(() => setLoading(false));
    // rarity check is free — show the verdict straight away
    setRarity(null);
    let alive = true;
    verifyRarity(id)
      .then((r) => alive && setRarity(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!tune) return;
    const s = tune.settings[settingIdx];
    const original = buildAbc({ name: tune.name, type: tune.type, key: s.key, meter: s.meter, abc: s.abc });
    originalAbcRef.current = original;
    if (pendingFavAbc.current) {
      setAbc(pendingFavAbc.current);
      pendingFavAbc.current = null;
    } else {
      setAbc(original);
    }
    setTransposeSteps(0);
  }, [tune, settingIdx]);

  const edited = abc !== "" && abc !== originalAbcRef.current;
  const currentFavKey = tune ? favKey(tune.tune_id, settingIdx + 1, edited, abc) : "";

  useEffect(() => {
    setFav(currentFavKey ? isFavourite(currentFavKey) : false);
  }, [currentFavKey]);

  // which versions already carry something of the user's (favourite / flag / edit)
  useEffect(() => {
    if (!tune) return;
    const update = () => {
      const marks = {};
      const add = (entry, label) => {
        const v = entry.version || 1;
        marks[v] = marks[v] || [];
        if (!marks[v].includes(label)) marks[v].push(label);
        if (entry.edited && !marks[v].includes("Edited")) marks[v].push("Edited");
      };
      favouritesForTune(tune.tune_id).forEach((f) => add(f, "Favourite"));
      flagsForTune(tune.tune_id).forEach((f) => add(f, "Flag"));
      setSavedMarks(marks);
    };
    update();
    window.addEventListener("fav-changed", update);
    window.addEventListener("flag-changed", update);
    return () => {
      window.removeEventListener("fav-changed", update);
      window.removeEventListener("flag-changed", update);
    };
  }, [tune]);

  const marksFor = (i) => {
    const list = [...(savedMarks[i + 1] || [])];
    if (edited && i === settingIdx && !list.includes("Edited")) list.push("Edited");
    return list;
  };

  const onFav = () => {
    if (!tune) return;
    toggleFavourite({
      key: currentFavKey,
      tune_id: tune.tune_id,
      name: tune.name,
      type: tune.type,
      version: settingIdx + 1,
      edited,
      abc: edited ? abc : originalAbcRef.current,
    });
    setFav((f) => !f);
    toast.success(fav ? "Removed from favourites" : `Saved${edited ? " (edited)" : ""} to favourites`);
  };

  const doTranspose = (delta) => {
    setAbc((prev) => transposeAbc(prev, delta));
    setTransposeSteps((s) => s + delta);
  };

  const copyAbc = async () => {
    try {
      await navigator.clipboard.writeText(abc);
      toast.success("ABC copied to clipboard");
    } catch {
      toast.error("Copy isn't allowed in this browser context");
    }
  };

  const resetAbc = () => {
    const s = tune.settings[settingIdx];
    setAbc(buildAbc({ name: tune.name, type: tune.type, key: s.key, meter: s.meter, abc: s.abc }));
    setTransposeSteps(0);
    toast.success("Notation reset to original");
  };

  const checkRarity = async () => {
    setRarityLoading(true);
    try {
      setRarity(await verifyRarity(id));
    } catch {
      toast.error("Rarity check failed");
    } finally {
      setRarityLoading(false);
    }
  };

  // keep the active version chip visible (e.g. opened via ?v=13)
  useEffect(() => {
    const row = versionRowRef.current;
    const chip = row?.querySelector("[data-active-version]");
    if (chip) {
      const r = row.getBoundingClientRect();
      const c = chip.getBoundingClientRect();
      row.scrollLeft += c.left - r.left - (r.width - c.width) / 2;
    }
  }, [settingIdx, tune, loading]);


  const scrollVersions = (dir) => {
    versionRowRef.current?.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[#6e655b]">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }
  if (!tune) return null;

  const s = tune.settings[settingIdx];

  return (
    <div className="min-h-screen pb-24">
      <AppHeader showFavourites={false} />
      {/* top bar */}
      <div className="sticky top-0 z-20 border-b border-[#e5e0d8] bg-[#fdfbf7]/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea]"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInfoOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea]"
              data-testid="info-button"
            >
              <Info className="h-4 w-4" /> Info
            </button>
            <button
              onClick={onFav}
              className="rounded-full border border-[#e5e0d8] bg-white p-2.5 transition-transform active:scale-125"
              data-testid="detail-favourite"
              aria-label="favourite"
            >
              <Star
                className="h-5 w-5"
                style={{ color: fav ? "#4CAF50" : "#c9c1b5", fill: fav ? "#4CAF50" : "none" }}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5">
        {/* title */}
        <div className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-4xl font-bold text-[#2d2824] sm:text-5xl" data-testid="tune-title">
              {tune.name}
            </h1>
            <TuneTypeBadge type={tune.type} size="lg" />
            {edited && (
              <span
                className="rounded-full bg-[#ff6b00] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white"
                data-testid="edited-badge"
              >
                Edited
              </span>
            )}
          </div>
        </div>

        {/* version carousel */}
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
            Version ({tune.settings.length} available)
          </h3>
          <div className="flex items-center gap-2">
            {tune.settings.length > 3 && (
              <button
                onClick={() => scrollVersions(-1)}
                className="hidden shrink-0 rounded-full border border-[#e5e0d8] bg-white p-2 text-[#2d2824] hover:bg-[#f3efea] sm:block"
                aria-label="scroll versions left"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div
              ref={versionRowRef}
              className="flex flex-1 gap-2 overflow-x-auto scroll-smooth px-1.5 pb-2 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="version-selector"
            >
              {tune.settings.map((st, i) => {
                const marks = marksFor(i);
                return (
                <button
                  key={st.setting_id}
                  data-active-version={i === settingIdx ? "1" : undefined}
                  onClick={() => setSettingIdx(i)}
                  className={`relative flex shrink-0 snap-start flex-col items-start rounded-2xl border px-4 py-2.5 text-left transition-colors ${
                    i === settingIdx
                      ? "border-[#2d2824] bg-[#2d2824] text-white"
                      : "border-[#e5e0d8] bg-white text-[#2d2824] hover:bg-[#f3efea]"
                  }`}
                  data-testid={`version-${i}`}
                >
                  {marks.length > 0 && (
                    <span
                      className="absolute -right-1.5 -top-1.5 h-3.5 w-3.5 rounded-full bg-[#4C1D95] ring-2 ring-[#fdfbf7]"
                      title={`Yours: ${marks.join(" · ")}`}
                      data-testid={`version-${i}-mark`}
                      data-marks={marks.join(",")}
                    />
                  )}
                  <span className="text-sm font-extrabold">Version {i + 1}</span>
                  <span className={`text-xs font-semibold ${i === settingIdx ? "text-white/70" : "text-[#a89e92]"}`}>
                    {prettyKey(st.key)} · {st.meter}
                  </span>
                </button>
                );
              })}
            </div>
            {tune.settings.length > 3 && (
              <button
                onClick={() => scrollVersions(1)}
                className="hidden shrink-0 rounded-full border border-[#e5e0d8] bg-white p-2 text-[#2d2824] hover:bg-[#f3efea] sm:block"
                aria-label="scroll versions right"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* notation zoom */}
        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-[#e5e0d8] bg-white px-4 py-2.5" data-testid="notation-zoom">
          <ZoomIn className="h-4 w-4 shrink-0 text-[#2d2824]" />
          <span className="shrink-0 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
            Notation zoom
          </span>
          <input
            type="range"
            min={10}
            max={200}
            step={10}
            value={notationZoom}
            onChange={(e) => setNotationZoom(Number(e.target.value))}
            className="fame-range"
            style={{ "--pct": `${((notationZoom - 10) / 190) * 100}%`, "--c": "#2d2824" }}
            data-testid="notation-zoom-slider"
          />
          <span className="w-10 shrink-0 text-right text-sm font-bold text-[#2d2824]" data-testid="notation-zoom-value">
            {notationZoom}%
          </span>
        </div>

        {/* editable ABC (hidden by default) */}
        {showAbc && (
          <div className="abc-fullscreen-editor mt-5" data-testid="editor-panel">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
                ABC notation (editable)
                <button
                  onClick={() => setHelpOpen(true)}
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-[#8a2be2] text-[11px] font-black text-[#8a2be2] transition-transform hover:scale-110 active:scale-95"
                  data-testid="abc-help-button"
                  aria-label="how ABC notation works"
                  title="How ABC notation works"
                >
                  i
                </button>
              </h3>
              <div className="flex items-center gap-2">
                {/* transpose */}
                <div className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white p-0.5" data-testid="transpose-control">
                  <button onClick={() => doTranspose(-1)} className="rounded-full p-1.5 text-[#2d2824] hover:bg-[#f3efea]" data-testid="transpose-down" aria-label="transpose down">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[44px] text-center text-xs font-bold text-[#6e655b]" data-testid="transpose-value">
                    {transposeSteps > 0 ? `+${transposeSteps}` : transposeSteps} st
                  </span>
                  <button onClick={() => doTranspose(1)} className="rounded-full p-1.5 text-[#2d2824] hover:bg-[#f3efea]" data-testid="transpose-up" aria-label="transpose up">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button onClick={copyAbc} className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]" data-testid="copy-abc">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
                <button onClick={resetAbc} className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]" data-testid="reset-abc">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </button>
                <button onClick={() => setAbcFull(true)} className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]" data-testid="abc-fullscreen">
                  <Maximize2 className="h-3.5 w-3.5" /> Fullscreen
                </button>
              </div>
            </div>
            <textarea
              value={abc}
              onChange={(e) => setAbc(e.target.value)}
              spellCheck={false}
              className="h-56 w-full resize-y rounded-2xl border border-[#e5e0d8] bg-white p-4 font-mono text-sm text-[#2d2824] outline-none focus:ring-2 focus:ring-[#2d2824]/20"
              data-testid="abc-editor"
            />
            <p className="mt-1 text-xs font-semibold text-[#a89e92]">
              Transpose and edits update the notation and player instantly.
            </p>
          </div>
        )}

        {/* player — with the music notation inside */}
        <div className="mt-6">
          <AbcPlayer abc={abc} showNotation tuneName={tune.name} zoom={notationZoom / 100} />
        </div>

        {/* action buttons */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => setShowAbc((v) => !v)}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
              showAbc ? "border-[#2d2824] bg-[#2d2824] text-white" : "border-[#e5e0d8] bg-white text-[#2d2824] hover:bg-[#f3efea]"
            }`}
            data-testid="toggle-abc"
          >
            <Code2 className="h-4 w-4" /> {showAbc ? "Hide ABC" : "Edit ABC"}
          </button>
          <button
            onClick={() => setPdfOpen(true)}
            className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea]"
            data-testid="export-pdf"
          >
            <FileDown className="h-4 w-4" /> Export PDF
          </button>
          <button
            onClick={checkRarity}
            disabled={rarityLoading}
            className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea] disabled:opacity-70"
            data-testid="rarity-check-button"
          >
            {rarityLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldQuestion className="h-4 w-4" />}
            Verify rarity
          </button>
          <FlagButton
            flagKey={currentFavKey}
            tune={tune}
            version={settingIdx + 1}
            edited={edited}
            abc={abc}
          />
          <FoundLinks tuneId={tune.tune_id} />
        </div>

        {/* rarity result */}
        {rarity && (
          <div
            className="mt-4 rounded-3xl border-2 p-5 float-up"
            style={{ backgroundColor: VERDICT_STYLES[rarity.verdict].bg, borderColor: VERDICT_STYLES[rarity.verdict].border }}
            data-testid="rarity-result"
          >
            <div className="flex items-center gap-2" style={{ color: VERDICT_STYLES[rarity.verdict].color }}>
              {(() => { const I = VERDICT_STYLES[rarity.verdict].icon; return <I className="h-5 w-5" />; })()}
              <span className="font-serif text-2xl font-bold">{VERDICT_STYLES[rarity.verdict].label}</span>
            </div>
            <p className="mt-2 text-sm font-semibold text-[#2d2824]">{rarity.message}</p>
            {rarity.different_type_matches?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-extrabold uppercase text-[#6e655b]">Famous same-name, different type</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {rarity.different_type_matches.map((m) => (
                    <button key={m.tune_id} onClick={() => navigate(`/tune/${m.tune_id}`)} className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] border border-[#e5e0d8] hover:bg-[#f3efea]">
                      {m.name} <TuneTypeBadge type={m.type} size="sm" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {rarity.same_type_matches?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-extrabold uppercase text-[#6e655b]">Famous same-name, same type</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {rarity.same_type_matches.map((m) => (
                    <button key={m.tune_id} onClick={() => navigate(`/tune/${m.tune_id}`)} className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] border border-[#e5e0d8] hover:bg-[#f3efea]">
                      {m.name} · fame {m.fame_score}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI expert */}
        <div className="mt-8">
          <AiExpertCard tuneId={tune.tune_id} tuneName={tune.name} abc={abc} />
        </div>
      </div>

      {/* Info dialog */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-lg bg-[#fdfbf7]" data-testid="info-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{tune.name}</DialogTitle>
            <DialogDescription className="capitalize">{tune.type} · thesession #{tune.tune_id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {tune.aliases?.length > 0 && (
              <div>
                <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">Also known as</p>
                <p className="mt-1 text-sm text-[#2d2824]">{tune.aliases.join(" · ")}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">Ranking</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="rounded-2xl bg-white border border-[#e5e0d8] p-3">
                  <div className="text-2xl font-bold font-serif text-[#e11d48]">{tune.fame_score}</div>
                  <div className="text-xs font-semibold text-[#6e655b]">Fame score</div>
                </div>
                <div className="rounded-2xl bg-white border border-[#e5e0d8] p-3">
                  <div className="text-2xl font-bold font-serif text-[#2d2824]">{tune.versions_count}</div>
                  <div className="text-xs font-semibold text-[#6e655b]">Versions</div>
                </div>
                <div className="rounded-2xl bg-white border border-[#e5e0d8] p-3">
                  <div className="text-2xl font-bold font-serif text-[#2d2824]">{tune.recordings_count}</div>
                  <div className="text-xs font-semibold text-[#6e655b]">Recordings</div>
                </div>
                <div className="rounded-2xl bg-white border border-[#e5e0d8] p-3">
                  <div className="text-2xl font-bold font-serif text-[#2d2824]">{tune.sets_count}</div>
                  <div className="text-xs font-semibold text-[#6e655b]">In sets</div>
                </div>
                <div className="rounded-2xl bg-white border border-[#e5e0d8] p-3">
                  <div className="text-2xl font-bold font-serif text-[#2d2824]">{tune.tunebooks}</div>
                  <div className="text-xs font-semibold text-[#6e655b]">Tunebooks</div>
                </div>
              </div>
              <div className="mt-2">
                <RankingVote
                  tuneId={tune.tune_id}
                  onScoreChange={(v) => setTune((t) => ({ ...t, fame_score: v }))}
                />
              </div>
            </div>
            <a href={tune.url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-full bg-[#2d2824] px-4 py-2.5 text-sm font-bold text-white">
              <ExternalLink className="h-4 w-4" /> Open on thesession.org
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF export dialog with real print preview + zoom */}
      <PdfExportDialog open={pdfOpen} onOpenChange={setPdfOpen} tune={tune} abc={abc} />

      {/* ABC notation cheat-sheet */}
      <AbcHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />

      {/* ABC editor fullscreen */}
      <FullscreenOverlay open={abcFull} onClose={() => setAbcFull(false)} title={`${tune.name} · ABC`}>
        <div className="mx-auto flex h-full max-w-4xl flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white p-0.5" data-testid="fs-transpose-control">
              <button onClick={() => doTranspose(-1)} className="rounded-full p-1.5 text-[#2d2824] hover:bg-[#f3efea]" data-testid="fs-transpose-down" aria-label="transpose down">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[44px] text-center text-xs font-bold text-[#6e655b]" data-testid="fs-transpose-value">
                {transposeSteps > 0 ? `+${transposeSteps}` : transposeSteps} st
              </span>
              <button onClick={() => doTranspose(1)} className="rounded-full p-1.5 text-[#2d2824] hover:bg-[#f3efea]" data-testid="fs-transpose-up" aria-label="transpose up">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <button onClick={copyAbc} className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]" data-testid="fs-copy-abc">
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
            <button onClick={resetAbc} className="flex items-center gap-1 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]" data-testid="fs-reset-abc">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button
              onClick={() => setHelpOpen(true)}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-[#8a2be2] text-xs font-black text-[#8a2be2] hover:scale-110"
              data-testid="fs-abc-help-button"
              aria-label="how ABC notation works"
            >
              i
            </button>
          </div>
          <textarea
            value={abc}
            onChange={(e) => setAbc(e.target.value)}
            spellCheck={false}
            className="min-h-[30vh] flex-1 resize-none rounded-2xl border border-[#e5e0d8] bg-white p-4 font-mono text-base text-[#2d2824] outline-none focus:ring-2 focus:ring-[#2d2824]/20"
            data-testid="abc-editor-fullscreen"
          />
          <div className="rounded-2xl border border-[#e5e0d8] bg-white p-4">
            <AbcNotation abc={abc} fit />
          </div>
        </div>
      </FullscreenOverlay>
    </div>
  );
}
